import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import MainCalculatorsList from "../MainCalculators";
import SharedHelmet from "../../Helmet";
import RelatedLinks from "../../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../../Ads";
const currencyTranslations = {
  SAR: "ريال سعودي",
  AED: "درهم إماراتي",
  ANG: "غيلدر أنتيلي هولندي",
  AUD: "دولار أسترالي",
  BHD: "دينار بحريني",
  CAD: "دولار كندي",
  CHF: "فرنك سويسري",
  CNY: "يوان صيني",
  DZD: "دينار جزائري",
  EGP: "جنيه مصري",
  EUR: "يورو",
  GBP: "جنيه إسترليني",
  GIP: "جنيه جبل طارق",
  INR: "روبي هندي",
  IQD: "دينار عراقي",
  IRR: "ريال إيراني",
  ISK: "كرون آيسلندية",
  JOD: "دينار أردني",
  JPY: "ين ياباني",
  KMF: "فرنك جزر القمر",
  KWD: "دينار كويتي",
  LBP: "ليرة لبنانية",
  LYD: "دينار ليبي",
  MAD: "درهم مغربي",
  NZD: "دولار نيوزيلندي",
  OMR: "ريال عماني",
  QAR: "ريال قطري",
  RUB: "روبل روسي",
  SDG: "جنيه سوداني",
  SEK: "كرون سويدي",
  SGD: "دولار سنغافوري",
  SOS: "شلن صومالي",
  SSP: "جنيه جنوب سوداني",
  SYP: "ليرة سورية",
  TND: "دينار تونسي",
  TRY: "ليرة تركية",
  USD: "دولار أمريكي",
  YER: "ريال يمني",
};
const currencySymbols = {
  SAR: "ر.س",
  AED: "د.إ",
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  CNY: "¥",
  EGP: "ج.م",
  KWD: "د.ك",
  BHD: "د.ب",
  OMR: "ر.ع",
  QAR: "ر.ق",
  JOD: "د.أ",
  LBP: "ل.ل",
  SYP: "ل.س",
  IQD: "د.ع",
  YER: "ر.ي",
  TND: "د.ت",
  DZD: "د.ج",
  MAD: "د.م",
  LYD: "د.ل",
  SDG: "ج.س",
  TRY: "₺",
  INR: "₹",
  CAD: "C$",
  AUD: "A$",
  CHF: "Fr",
  SEK: "kr",
  RUB: "₽",
};
function CurrencyConverter() {
  const { fromCurrency, toCurrency } = useParams();
  const [amount, setAmount] = useState(1);
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [rate, setRate] = useState(null);
  const [storedAmount, setStoredAmount] = useState("");
  const [lastUpdate, setLastUpdate] = useState(null);
  const translatedFromCurrency =
    currencyTranslations[fromCurrency] || fromCurrency;
  const translatedToCurrency = currencyTranslations[toCurrency] || toCurrency;
  const fromSymbol = currencySymbols[fromCurrency] || fromCurrency;
  const toSymbol = currencySymbols[toCurrency] || toCurrency;
  const currentYear = new Date().getFullYear();
  const TITLE = `تحويل من ${translatedFromCurrency} إلى ${translatedToCurrency} ${currentYear} - محول العملات المباشر`;
  const DESCRIPTION = `احصل على أحدث أسعار صرف العملات من ${translatedFromCurrency} (${fromCurrency}) إلى ${translatedToCurrency} (${toCurrency}) ${currentYear}. محول عملات دقيق ومجاني مع تحديثات لحظية. حول أي مبلغ من ${translatedFromCurrency} إلى ${translatedToCurrency} باستخدام سعر الصرف الحالي. أداة موثوقة لتحويل العملات الأجنبية والعربية.`;
  const KEYWORDS = `تحويل عملات, سعر صرف ${fromCurrency} إلى ${toCurrency}, محول عملات, تحويل من ${translatedFromCurrency} إلى ${translatedToCurrency}, أسعار الصرف اليوم ${currentYear}, سعر ${translatedToCurrency} مقابل ${translatedFromCurrency}, تحويل العملات الأجنبية, محول ${fromCurrency} ${toCurrency}, حاسبة العملات, أسعار الصرف المباشرة, ${fromCurrency} to ${toCurrency}, currency converter, تحويل الأموال, سعر الصرف الحالي, ${translatedFromCurrency} ${translatedToCurrency} اليوم`;
  const OG_URL = `https://do-calculate.com/calculator/ar/currency_converter/${fromCurrency}/${toCurrency}`;
  useEffect(() => {
    const fetchConversionRate = async () => {
      try {
        const response = await fetch(
          `https://api.exchangerate-api.com/v4/latest/${fromCurrency}`
        );
        const data = await response.json();
        setRate(data.rates[toCurrency]);
        setLastUpdate(new Date(data.time_last_updated));
      } catch (error) {
        console.error("Error fetching conversion rate:", error);
      }
    };
    fetchConversionRate();
  }, [fromCurrency, toCurrency]);
  const handleCalculate = () => {
    if (amount && rate) {
      setStoredAmount(amount);
      setConvertedAmount((amount * rate).toFixed(2));
    }
  };
  const amounts = [
    1, 2, 3, 4, 5, 10, 15, 20, 25, 50, 75, 100, 125, 150, 175, 200, 225, 250,
    275, 300, 325, 350, 375, 400, 425, 450, 475, 500, 525, 550, 575, 600, 625,
    650, 675, 700, 725, 750, 775, 800, 825, 850, 875, 900, 925, 950, 975, 1000,
    1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 5500, 6000, 6500, 7000,
    7500, 8000, 8500, 9000, 9500, 10000,
  ];
  const conversionTable = amounts.map((a) => ({
    fromAmount: a,
    toAmount: rate ? (a * rate).toFixed(2) : "...",
  }));
  // Enhanced Schema.org structured data
  const generateSchemaData = () => {
    if (!rate) return null;
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "الصفحة الرئيسية",
          item: "https://do-calculate.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "محول العملات",
          item: "https://do-calculate.com/calculator/ar/currency_list/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: `تحويل من ${translatedFromCurrency} إلى ${translatedToCurrency}`,
          item: OG_URL,
        },
      ],
    };
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: `ما هو سعر صرف ${translatedFromCurrency} مقابل ${translatedToCurrency}؟`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `سعر الصرف الحالي هو: 1 ${translatedFromCurrency} = ${rate} ${translatedToCurrency}`,
          },
        },
        {
          "@type": "Question",
          name: "كيف يتم تحديد سعر صرف العملات؟",
          acceptedAnswer: {
            "@type": "Answer",
            text: "تتحدد أسعار صرف العملات بناءً على العرض والطلب في سوق الفوركس العالمي، بالإضافة إلى عوامل اقتصادية مثل أسعار الفائدة، التضخم، الاستقرار السياسي، والنمو الاقتصادي.",
          },
        },
        {
          "@type": "Question",
          name: "هل أسعار الصرف تتغير باستمرار؟",
          acceptedAnswer: {
            "@type": "Answer",
            text: "نعم، أسعار صرف العملات تتغير باستمرار على مدار الساعة في سوق الفوركس العالمي، وتتأثر بالأخبار الاقتصادية والسياسية العالمية.",
          },
        },
        {
          "@type": "Question",
          name: "ما الفرق بين سعر الصرف الرسمي وسعر السوق؟",
          acceptedAnswer: {
            "@type": "Answer",
            text: "سعر الصرف الرسمي يحدده البنك المركزي، بينما سعر السوق (السوق الموازية) يتحدد بالعرض والطلب. الفرق بينهما يعتمد على السياسات النقدية والاقتصادية للدولة.",
          },
        },
        {
          "@type": "Question",
          name: "كيف أحصل على أفضل سعر صرف؟",
          acceptedAnswer: {
            "@type": "Answer",
            text: "للحصول على أفضل سعر صرف: قارن بين عدة مصارف وشركات صرافة، تجنب الصرافة في المطارات، استخدم بطاقات الائتمان بدون عمولات أجنبية، وراقب الأسعار لتحويل الأموال في الوقت المناسب.",
          },
        },
      ],
    };
    const calculatorSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: `محول العملات من ${translatedFromCurrency} إلى ${translatedToCurrency}`,
      description: DESCRIPTION,
      url: OG_URL,
      applicationCategory: "FinanceApplication",
      operatingSystem: "Any",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    };
    return [breadcrumbSchema, faqSchema, calculatorSchema];
  };
  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={generateSchemaData()}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "12px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            {/* Introduction */}
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                مرحباً بك في <strong>محول العملات المتقدم</strong> من{" "}
                <strong>{translatedFromCurrency}</strong> ({fromCurrency}) إلى{" "}
                <strong>{translatedToCurrency}</strong> ({toCurrency}). باستخدام
                هذه الأداة المجانية، يمكنك <strong>تحويل العملات</strong> بسهولة
                ودقة بناءً على <strong>أحدث أسعار الصرف</strong> من الأسواق
                المالية العالمية. سواء كنت مسافراً، متداولاً في{" "}
                <strong>سوق الفوركس</strong>، أو تحتاج لمعرفة قيمة عملتك، نوفر
                لك <strong>سعر الصرف الحالي</strong> بدقة وموثوقية عالية.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            {/* Calculator Section */}
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-3 bg-success text-white text-center rounded">
                  <h2 style={{ fontSize: "18px", margin: 0 }}>
                    سعر صرف {translatedFromCurrency} مقابل {translatedToCurrency}
                  </h2>
                </div>
                <div className="alert alert-info" style={{ fontSize: "17px" }}>
                  <strong>1</strong> {translatedFromCurrency} ({fromSymbol}) يساوي{" "}
                  <strong>{rate ? rate.toFixed(4) : "..."}</strong>{" "}
                  {translatedToCurrency} ({toSymbol})
                </div>
                <hr />
                <div className="form-group mb-3">
                  <label  htmlFor="amount">
                    <strong>أدخل المبلغ بالـ {translatedFromCurrency}:</strong>
                  </label>
                  <input
                    id="amount"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="form-control"
                    min="0"
                    step="0.01"
                    placeholder={`مثال: 100`}
                  />
                </div>
                <div className="text-center mb-3 mt-4">
                  <button
                    className="btn bg-success text-white"
                    onClick={handleCalculate}
                    style={{
                      fontSize: "18px",
                      padding: "10px 30px",
                    }}
                  >
                    احسب                  </button>
                </div>
                {convertedAmount &&  (
                  <div
                    className="alert alert-success"
                    style={{ fontSize: "18px", textAlign: "center", lineHeight: "1.8" }}
                  >
                    <strong>النتيجة:</strong>
                    <br />
                    <strong style={{ fontSize: "20px" }}>
                      {storedAmount} {translatedFromCurrency}
                    </strong>{" "}
                    ={" "}
                    <strong style={{ fontSize: "20px", color: "#1a5f3f" }}>
                      {convertedAmount} {translatedToCurrency}
                    </strong>
                  </div>
                )}
                <hr />
                <div className="text-center">
                  <a
                    className="btn btn-secondary"
                    href={`/calculator/ar/currency_converter/${toCurrency}/${fromCurrency}`}
                    style={{ fontSize: "16px", padding: "10px 20px" }}
                  >
                    🔄 تحويل من {translatedToCurrency} إلى {translatedFromCurrency}
                  </a>
                </div>
              </div>
            </div>
            <hr />
            <div
              className="alert alert-warning"
              style={{ fontSize: "15px", lineHeight: "1.8" }}
            >
              <strong>⚠️ ملاحظة مهمة:</strong> <strong>أسعار الصرف</strong>{" "}
              تتغير باستمرار في سوق العملات العالمي. الأسعار المعروضة هي{" "}
              <strong>أسعار مرجعية</strong> وقد تختلف عن الأسعار في البنوك أو
              شركات الصرافة. استخدم هذه الأداة للإشارة فقط، وتحقق من الأسعار
              الفعلية قبل إجراء أي معاملة مالية.
            </div>
            <hr />
            {/* Conversion Table */}
            <div className="mb-4">
              <h3
                className="p-3 bg-success text-white text-center rounded"
                style={{ fontSize: "18px", margin: 0 }}
              >
                جدول تحويل {translatedFromCurrency} إلى {translatedToCurrency}
              </h3>
              <p
                className="alert alert-success mt-3"
                style={{ lineHeight: "1.8" }}
              >
                كل <strong>1 (واحد)</strong> {translatedFromCurrency} يساوي{" "}
                <strong>{rate ? rate.toFixed(4) : "..."}</strong>{" "}
                {translatedToCurrency} في أسواق المال العربية والعالمية. فيما
                يلي جدول يوضح <strong>سعر الصرف</strong> لمبالغ مختلفة:
              </p>
              <table className="bmi-table" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th className="bmi-header" style={{ textAlign: "center" }}>
                      {translatedFromCurrency} ({fromSymbol})
                    </th>
                    <th className="bmi-header" style={{ textAlign: "center" }}>
                      {translatedToCurrency} ({toSymbol})
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {conversionTable.map((entry, index) => (
                    <tr key={index}>
                      <td style={{ textAlign: "center", padding: "12px" }}>
                        <strong>{entry.fromAmount}</strong> {fromSymbol}
                      </td>
                      <td style={{ textAlign: "center", padding: "12px" }}>
                        <strong>{entry.toAmount}</strong> {toSymbol}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <hr />
            {/* Related Links */}
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      {/* Educational Content Section */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>ما هو محول العملات؟ كيف يعمل؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>محول العملات</strong> هو أداة إلكترونية مجانية تساعدك
                  على <strong>تحويل العملات</strong> من عملة إلى أخرى بناءً على
                  <strong> سعر الصرف الحالي</strong>. يستخدم المحول{" "}
                  <strong>أسعار الصرف المباشرة</strong> من الأسواق المالية
                  العالمية لتوفير نتائج دقيقة ومحدثة.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  كيف يعمل محول العملات؟
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>الحصول على سعر الصرف:</strong> يتصل المحول بواجهة
                    برمجية (API) تحصل على <strong>أسعار الصرف الحالية</strong> من
                    سوق الفوركس العالمي.
                  </li>
                  <li>
                    <strong>إدخال المبلغ:</strong> تدخل المبلغ الذي تريد تحويله من
                    العملة الأولى.
                  </li>
                  <li>
                    <strong>عملية الحساب:</strong> يضرب المحول المبلغ في{" "}
                    <strong>سعر الصرف</strong> الحالي للحصول على القيمة المقابلة في
                    العملة الثانية.
                  </li>
                  <li>
                    <strong>عرض النتيجة:</strong> تظهر لك النتيجة فوراً بدقة عالية.
                  </li>
                </ol>
                <div
                  className="alert alert-info mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>💡 مثال عملي:</strong> إذا كان سعر الصرف من{" "}
                  <strong>{translatedFromCurrency}</strong> إلى{" "}
                  <strong>{translatedToCurrency}</strong> هو {rate ? rate.toFixed(4) : "..."}
                  ، وأردت تحويل 100 {translatedFromCurrency}، فالنتيجة ستكون: 100 ×{" "}
                  {rate ? rate.toFixed(4) : "..."} ={" "}
                  {rate ? (100 * rate).toFixed(2) : "..."}{" "}
                  {translatedToCurrency}.
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  لماذا تستخدم محول العملات؟
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>السفر الدولي:</strong> معرفة قيمة عملتك في بلد الوجهة
                  </li>
                  <li>
                    <strong>التجارة الإلكترونية:</strong> حساب أسعار المنتجات
                    بعملتك المحلية
                  </li>
                  <li>
                    <strong>التحويلات المالية:</strong> معرفة المبلغ الذي سيصل
                    للمستلم
                  </li>
                  <li>
                    <strong>الاستثمار:</strong> متابعة قيمة استثماراتك بالعملات
                    الأجنبية
                  </li>
                  <li>
                    <strong>التخطيط المالي:</strong> إعداد الميزانية للسفر أو
                    الدراسة بالخارج
                  </li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>العوامل المؤثرة على سعر صرف العملات</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  تتأثر <strong>أسعار صرف العملات</strong> بمجموعة معقدة من العوامل
                  الاقتصادية والسياسية. فهم هذه العوامل يساعدك على اتخاذ قرارات
                  مالية أفضل.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  1. العوامل الاقتصادية
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>أسعار الفائدة:</strong> ارتفاع أسعار الفائدة يجذب
                    الاستثمارات الأجنبية ويرفع قيمة العملة
                  </li>
                  <li>
                    <strong>التضخم:</strong> التضخم المرتفع يخفض قيمة العملة، بينما
                    التضخم المنخفض يرفعها
                  </li>
                  <li>
                    <strong>النمو الاقتصادي:</strong> الاقتصاد القوي يجذب
                    الاستثمارات ويرفع قيمة العملة
                  </li>
                  <li>
                    <strong>الميزان التجاري:</strong> الفائض في الميزان التجاري
                    يقوي العملة، والعجز يضعفها
                  </li>
                  <li>
                    <strong>الدين العام:</strong> الديون المرتفعة تقلل ثقة
                    المستثمرين وتضعف العملة
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  2. العوامل السياسية
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>الاستقرار السياسي:</strong> الاستقرار يعزز ثقة
                    المستثمرين ويقوي العملة
                  </li>
                  <li>
                    <strong>السياسات الحكومية:</strong> السياسات الاقتصادية
                    الحكيمة تدعم قيمة العملة
                  </li>
                  <li>
                    <strong>العلاقات الدولية:</strong> العلاقات الجيدة تشجع
                    التجارة والاستثمار
                  </li>
                  <li>
                    <strong>الأزمات والحروب:</strong> تؤدي إلى هروب رؤوس الأموال
                    وضعف العملة
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  3. العرض والطلب
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>الطلب على العملة:</strong> زيادة الطلب ترفع قيمة العملة
                  </li>
                  <li>
                    <strong>المضاربة:</strong> توقعات المتداولين تؤثر على الأسعار
                  </li>
                  <li>
                    <strong>التدخل الحكومي:</strong> البنوك المركزية قد تتدخل
                    لتعديل سعر الصرف
                  </li>
                  <li>
                    <strong>التجارة الدولية:</strong> الصادرات والواردات تؤثر على
                    العرض والطلب
                  </li>
                </ul>
                <div
                  className="alert alert-success mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>✓ نصيحة:</strong> تابع الأخبار الاقتصادية والسياسية
                  لفهم تحركات <strong>أسعار الصرف</strong>. استخدم{" "}
                  <strong>محول العملات</strong> بانتظام لمتابعة التغيرات.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>أنواع أسعار الصرف ونظم الصرف</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  أنواع أسعار الصرف:
                </h3>
                <div
                  className="alert alert-info"
                  style={{ lineHeight: "1.8", marginTop: "10px" }}
                >
                  <h4 style={{ fontSize: "16px" }}>
                    <strong>1. سعر الصرف الثابت (Fixed Exchange Rate)</strong>
                  </h4>
                  <p>
                    تحدد الحكومة أو البنك المركزي سعر صرف ثابت مقابل عملة أخرى
                    (عادة الدولار الأمريكي أو اليورو). تتدخل الدولة في السوق
                    للحفاظ على هذا السعر.
                  </p>
                  <p>
                    <strong>مثال:</strong> الريال السعودي مرتبط بالدولار الأمريكي
                    (1 دولار = 3.75 ريال).
                  </p>
                  <p>
                    <strong>المزايا:</strong> استقرار الأسعار، تشجيع التجارة
                    الدولية
                    <br />
                    <strong>العيوب:</strong> يتطلب احتياطيات ضخمة من العملات
                    الأجنبية
                  </p>
                </div>
                <div
                  className="alert alert-warning"
                  style={{ lineHeight: "1.8", marginTop: "10px" }}
                >
                  <h4 style={{ fontSize: "16px" }}>
                    <strong>2. سعر الصرف المرن/العائم (Floating Exchange Rate)</strong>
                  </h4>
                  <p>
                    يتحدد سعر الصرف بناءً على العرض والطلب في سوق العملات دون
                    تدخل حكومي مباشر.
                  </p>
                  <p>
                    <strong>مثال:</strong> الدولار الأمريكي، اليورو، الجنيه
                    الإسترليني
                  </p>
                  <p>
                    <strong>المزايا:</strong> يتكيف تلقائياً مع الظروف الاقتصادية
                    <br />
                    <strong>العيوب:</strong> تقلبات كبيرة قد تؤثر على الاستقرار
                    الاقتصادي
                  </p>
                </div>
                <div
                  className="alert alert-success"
                  style={{ lineHeight: "1.8", marginTop: "10px" }}
                >
                  <h4 style={{ fontSize: "16px" }}>
                    <strong>3. سعر الصرف المدار (Managed Float)</strong>
                  </h4>
                  <p>
                    نظام هجين حيث يتحرك سعر الصرف بحرية لكن البنك المركزي يتدخل
                    أحياناً لتوجيه السعر.
                  </p>
                  <p>
                    <strong>مثال:</strong> الجنيه المصري، الليرة التركية
                  </p>
                </div>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  مصطلحات هامة:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>سعر الشراء (Bid):</strong> السعر الذي يشتري به البنك
                    العملة منك
                  </li>
                  <li>
                    <strong>سعر البيع (Ask):</strong> السعر الذي يبيع به البنك
                    العملة لك
                  </li>
                  <li>
                    <strong>الفارق (Spread):</strong> الفرق بين سعر الشراء والبيع
                    (ربح البنك)
                  </li>
                  <li>
                    <strong>سعر الصرف المتقاطع (Cross Rate):</strong> سعر الصرف بين
                    عملتين من خلال عملة ثالثة
                  </li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>نصائح ذكية لتحويل العملات وتوفير المال</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  نصائح للحصول على أفضل سعر صرف:
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>قارن الأسعار:</strong> قارن أسعار الصرف بين عدة بنوك
                    وشركات صرافة قبل التحويل.
                  </li>
                  <li>
                    <strong>تجنب المطارات:</strong> أسعار الصرف في مكاتب الصرافة
                    بالمطارات عادة الأسوأ (فارق قد يصل 10-15%).
                  </li>
                  <li>
                    <strong>استخدم البطاقات البنكية:</strong> بطاقات الائتمان
                    والخصم توفر أسعار صرف أفضل عادة.
                  </li>
                  <li>
                    <strong>احذر العمولات الخفية:</strong> اسأل عن جميع الرسوم
                    قبل إتمام الصفقة.
                  </li>
                  <li>
                    <strong>راقب الأسواق:</strong> تابع أسعار الصرف واختر الوقت
                    المناسب للتحويل.
                  </li>
                  <li>
                    <strong>استخدم خدمات التحويل الإلكترونية:</strong> منصات مثل
                    Wise أو Revolut توفر أسعار أفضل من البنوك التقليدية.
                  </li>
                </ol>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  أخطاء شائعة يجب تجنبها:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>عدم التخطيط المسبق:</strong> الانتظار للحظة الأخيرة قد
                    يجبرك على قبول سعر سيء.
                  </li>
                  <li>
                    <strong>تجاهل الرسوم:</strong> التركيز فقط على سعر الصرف وتجاهل
                    العمولات والرسوم.
                  </li>
                  <li>
                    <strong>الصرافة في الفنادق:</strong> عادة تكون الأسعار سيئة
                    جداً.
                  </li>
                  <li>
                    <strong>عدم السؤال عن السعر النهائي:</strong> اسأل دائماً:
                    "كم سأحصل بالضبط بعد كل الرسوم؟"
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  للمسافرين:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    صرّف مبلغاً صغيراً للضرورات الأولى فقط عند الوصول
                  </li>
                  <li>استخدم أجهزة الصراف الآلي للحصول على أسعار أفضل</li>
                  <li>احمل بطاقتين ائتمانيتين على الأقل للطوارئ</li>
                  <li>تجنب الدفع بعملتك المحلية في الخارج (غالباً سعر سيء)</li>
                </ul>
                <div
                  className="alert alert-warning mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>⚠️ تحذير من الاحتيال:</strong> احذر من عروض سعر الصرف
                  "الممتازة جداً" - قد تكون عملية احتيال. تعامل فقط مع جهات
                  مرخصة وموثوقة.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>سوق الفوركس: فهم سوق صرف العملات العالمي</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  ما هو سوق الفوركس (Forex/FX)؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>سوق الفوركس</strong> (Foreign Exchange Market) هو أكبر
                  سوق مالي في العالم، حيث يتم تداول العملات على مدار 24 ساعة يومياً،
                  5 أيام في الأسبوع. يبلغ حجم التداول اليومي أكثر من{" "}
                  <strong>6 تريليون دولار</strong>!
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  خصائص سوق الفوركس:
                </h3>
                <div
                  className="alert alert-info"
                  style={{ lineHeight: "1.8", marginTop: "10px" }}
                >
                  <ul>
                    <li>
                      <strong>لامركزي:</strong> لا يوجد موقع فعلي واحد، بل يعمل عبر
                      شبكة عالمية من البنوك
                    </li>
                    <li>
                      <strong>يعمل 24 ساعة:</strong> يفتح في سيدني ويغلق في نيويورك
                    </li>
                    <li>
                      <strong>سيولة عالية:</strong> يمكن شراء وبيع كميات ضخمة فوراً
                    </li>
                    <li>
                      <strong>رافعة مالية:</strong> يمكن التداول بمبالغ أكبر من
                      رأس المال (خطر عالي!)
                    </li>
                  </ul>
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  أوقات التداول الرئيسية:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>جلسة آسيا:</strong> طوكيو، سنغافورة، هونغ كونغ
                  </li>
                  <li>
                    <strong>جلسة أوروبا:</strong> لندن، فرانكفورت (الأكثر نشاطاً)
                  </li>
                  <li>
                    <strong>جلسة أمريكا:</strong> نيويورك، تورنتو
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  العملات الرئيسية (Majors):
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>USD</strong> - الدولار الأمريكي (أكثر العملات تداولاً)
                  </li>
                  <li>
                    <strong>EUR</strong> - اليورو
                  </li>
                  <li>
                    <strong>JPY</strong> - الين الياباني
                  </li>
                  <li>
                    <strong>GBP</strong> - الجنيه الإسترليني
                  </li>
                  <li>
                    <strong>CHF</strong> - الفرنك السويسري
                  </li>
                  <li>
                    <strong>AUD</strong> - الدولار الأسترالي
                  </li>
                  <li>
                    <strong>CAD</strong> - الدولار الكندي
                  </li>
                </ul>
                <div
                  className="alert alert-warning mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>⚠️ تحذير للمبتدئين:</strong> تداول العملات (الفوركس)
                  ينطوي على مخاطر عالية وقد يؤدي لخسارة كامل رأس المال. لا تتداول
                  إلا بأموال تستطيع تحمل خسارتها، وتعلم جيداً قبل البدء.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>معلومات وحقائق مثيرة عن العملات</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  حقائق تاريخية:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>أول عملة معدنية:</strong> سُكت في مملكة ليديا (تركيا
                    الحالية) حوالي 600 قبل الميلاد.
                  </li>
                  <li>
                    <strong>أول عملة ورقية:</strong> استخدمها الصينيون في القرن
                    السابع الميلادي.
                  </li>
                  <li>
                    <strong>معيار الذهب:</strong> حتى عام 1971، كانت معظم العملات
                    مرتبطة بالذهب.
                  </li>
                  <li>
                    <strong>اليورو:</strong> أصبح عملة رسمية في 1999، ويستخدمه
                    الآن 19 دولة أوروبية.
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  أرقام مذهلة:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>حجم سوق الفوركس:</strong> أكثر من 6 تريليون دولار
                    يومياً!
                  </li>
                  <li>
                    <strong>الدولار الأمريكي:</strong> يشارك في 88% من جميع
                    صفقات الفوركس
                  </li>
                  <li>
                    <strong>عدد العملات:</strong> هناك حوالي 180 عملة رسمية في
                    العالم
                  </li>
                  <li>
                    <strong>أقدم عملة:</strong> الجنيه الإسترليني (أكثر من 1200
                    سنة!)
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  عملات غريبة ومثيرة:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>أغلى عملة:</strong> الدينار الكويتي (1 KWD ≈ 3.30 USD)
                  </li>
                  <li>
                    <strong>أرخص عملة:</strong> الريال الإيراني والبوليفار
                    الفنزويلي
                  </li>
                  <li>
                    <strong>عملة بيتكوين:</strong> أول عملة رقمية لامركزية (2009)
                  </li>
                  <li>
                    <strong>الدولار السنغافوري:</strong> مصنوع من البوليمر
                    (بلاستيك) بدلاً من الورق
                  </li>
                </ul>
                <div
                  className="alert alert-info mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>🌍 هل تعلم؟</strong> الدولار الأمريكي ليس العملة الرسمية
                  للولايات المتحدة فقط، بل يستخدم أيضاً كعملة رسمية أو شبه رسمية
                  في أكثر من 10 دول أخرى!
                </div>
              </div>
            </div>
          </div>
          <br />
          {/* FAQ Section */}
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>
            أسئلة متكررة حول تحويل العملات
          </h2>
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  ما هو سعر صرف {translatedFromCurrency} مقابل{" "}
                  {translatedToCurrency}؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>سعر الصرف الحالي</strong> هو: <strong>1</strong>{" "}
                  {translatedFromCurrency} ({fromSymbol}) ={" "}
                  <strong>{rate ? rate.toFixed(4) : "جاري التحميل..."}</strong>{" "}
                  {translatedToCurrency} ({toSymbol}).
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  هذا يعني أنه إذا قمت بتحويل <strong>100</strong>{" "}
                  {translatedFromCurrency}، ستحصل على حوالي{" "}
                  <strong>{rate ? (100 * rate).toFixed(2) : "..."}</strong>{" "}
                  {translatedToCurrency}.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>ملاحظة:</strong> الأسعار تتغير باستمرار في{" "}
                  <strong>سوق الفوركس</strong>. استخدم <strong>محول العملات</strong>{" "}
                  أعلاه للحصول على أحدث الأسعار.
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  كيف يتم تحديد سعر صرف العملات؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  يتحدد <strong>سعر صرف العملات</strong> بناءً على عدة عوامل:
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>العرض والطلب:</strong> في سوق الفوركس العالمي، العملات
                    التي عليها طلب أكبر ترتفع قيمتها.
                  </li>
                  <li>
                    <strong>أسعار الفائدة:</strong> البنوك المركزية التي ترفع أسعار
                    الفائدة تجذب استثمارات أجنبية، مما يرفع قيمة عملتها.
                  </li>
                  <li>
                    <strong>التضخم:</strong> الدول ذات التضخم المنخفض عادة ما تكون
                    عملاتها أقوى.
                  </li>
                  <li>
                    <strong>الاستقرار السياسي والاقتصادي:</strong> الدول المستقرة
                    تجذب المزيد من الاستثمارات.
                  </li>
                  <li>
                    <strong>الميزان التجاري:</strong> الدول التي تصدر أكثر مما
                    تستورد عادة ما تكون عملاتها أقوى.
                  </li>
                </ol>
                <p style={{ lineHeight: "1.8", marginTop: "10px" }}>
                  تتفاعل كل هذه العوامل معاً لتحديد <strong>سعر الصرف</strong> في
                  كل لحظة.
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  هل أسعار الصرف تتغير باستمرار؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>نعم</strong>، <strong>أسعار صرف العملات</strong> تتغير
                  باستمرار على مدار الساعة، 24/5 (من الاثنين حتى الجمعة).
                </p>
                <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                  لماذا تتغير الأسعار؟
                </h4>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>التداول المستمر:</strong> ملايين المعاملات تحدث كل
                    ثانية في سوق الفوركس
                  </li>
                  <li>
                    <strong>الأخبار الاقتصادية:</strong> إعلانات البنوك المركزية،
                    أرقام التوظيف، معدلات النمو - كلها تؤثر فوراً
                  </li>
                  <li>
                    <strong>الأحداث السياسية:</strong> الانتخابات، الأزمات،
                    القرارات الحكومية
                  </li>
                  <li>
                    <strong>المضاربة:</strong> المتداولون يتوقعون التغيرات
                    ويتداولون بناءً عليها
                  </li>
                </ul>
                <div
                  className="alert alert-info mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>💡 نصيحة:</strong> لهذا السبب من المهم استخدام{" "}
                  <strong>محول العملات</strong> للحصول على آخر الأسعار قبل أي
                  معاملة مالية.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  ما الفرق بين سعر الصرف الرسمي وسعر السوق؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  هناك نوعان من <strong>أسعار الصرف</strong>:
                </p>
                <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                  <strong>1. سعر الصرف الرسمي:</strong>
                </h4>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>يحدده البنك المركزي أو الحكومة</li>
                  <li>يستخدم في المعاملات الحكومية الرسمية</li>
                  <li>ثابت نسبياً أو يتغير ببطء</li>
                  <li>مثال: سعر الصرف في البنوك الحكومية</li>
                </ul>
                <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                  <strong>2. سعر السوق (السوق الموازية/السوداء):</strong>
                </h4>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>يتحدد بالعرض والطلب الفعلي</li>
                  <li>يتغير باستمرار</li>
                  <li>عادة أعلى من السعر الرسمي في الدول التي تتحكم بالعملة</li>
                  <li>مثال: سعر الصرف في محلات الصرافة</li>
                </ul>
                <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                  <strong>الفرق بينهما:</strong>
                </h4>
                <p style={{ lineHeight: "1.8" }}>
                  في الدول التي تطبق نظام <strong>سعر الصرف الثابت</strong> مع
                  قيود على العملة، قد يكون هناك فرق كبير بين السعرين. في الدول
                  ذات <strong>سعر الصرف المرن</strong>، الفرق يكون عادة صغيراً
                  (فقط عمولة البنك).
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  كيف أحصل على أفضل سعر صرف؟
                </h3>
              </div>
              <div className="info-section-body">
                <h4 style={{ fontSize: "16px", marginBottom: "10px" }}>
                  <strong>نصائح عملية للحصول على أفضل سعر:</strong>
                </h4>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>قارن الأسعار:</strong>
                    <ul style={{ marginTop: "5px" }}>
                      <li>تحقق من عدة بنوك</li>
                      <li>قارن مكاتب الصرافة</li>
                      <li>راجع المنصات الإلكترونية</li>
                    </ul>
                  </li>
                  <li>
                    <strong>تجنب هذه الأماكن:</strong>
                    <ul style={{ marginTop: "5px" }}>
                      <li>❌ مكاتب الصرافة في المطارات</li>
                      <li>❌ الفنادق</li>
                      <li>❌ المناطق السياحية</li>
                    </ul>
                  </li>
                  <li>
                    <strong>استخدم البطاقات الذكية:</strong>
                    <ul style={{ marginTop: "5px" }}>
                      <li>بطاقات بدون عمولات أجنبية</li>
                      <li>بطاقات السفر متعددة العملات</li>
                      <li>المحافظ الإلكترونية</li>
                    </ul>
                  </li>
                  <li>
                    <strong>اختر الوقت المناسب:</strong>
                    <ul style={{ marginTop: "5px" }}>
                      <li>تابع الأخبار الاقتصادية</li>
                      <li>راقب اتجاهات السوق</li>
                      <li>حوّل في أوقات استقرار السوق</li>
                    </ul>
                  </li>
                  <li>
                    <strong>اسأل عن السعر النهائي:</strong> اطلب دائماً المبلغ
                    النهائي الذي ستحصل عليه بعد كل الرسوم والعمولات.
                  </li>
                </ol>
                <div
                  className="alert alert-success mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>✓ نصيحة ذهبية:</strong> استخدم <strong>محول العملات</strong>{" "}
                  لمعرفة السعر الحقيقي، ثم قارنه بما يعرضه البنك أو الصرافة. إذا
                  كان الفرق أكثر من 3-5%، ابحث عن خيار أفضل!
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  ما هي الرسوم والعمولات عند تحويل العملات؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  عند <strong>تحويل العملات</strong>، هناك عدة أنواع من الرسوم قد
                  تُفرض عليك:
                </p>
                <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                  <strong>1. فارق السعر (Spread):</strong>
                </h4>
                <p style={{ lineHeight: "1.8" }}>
                  الفرق بين سعر الشراء والبيع. هذا هو <strong>الربح الرئيسي</strong>{" "}
                  للبنك أو الصرافة. قد يتراوح من 1% إلى 10% أو أكثر.
                </p>
                <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                  <strong>2. عمولة ثابتة:</strong>
                </h4>
                <p style={{ lineHeight: "1.8" }}>
                  مبلغ ثابت يُخصم من كل معاملة (مثلاً: 5 دولار لكل تحويل)، بغض
                  النظر عن المبلغ.
                </p>
                <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                  <strong>3. عمولة نسبية:</strong>
                </h4>
                <p style={{ lineHeight: "1.8" }}>
                  نسبة مئوية من المبلغ المحوّل (مثلاً: 2% من المبلغ).
                </p>
                <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                  <strong>4. رسوم خفية:</strong>
                </h4>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>رسوم التحويل البنكي (Wire Transfer Fees)</li>
                  <li>رسوم الوسيط (Intermediary Bank Fees)</li>
                  <li>رسوم العملات الأجنبية على البطاقات (Foreign Transaction Fees)</li>
                  <li>رسوم السحب من الصراف الآلي</li>
                </ul>
                <div
                  className="alert alert-warning mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>⚠️ انتبه:</strong> قد تصل الرسوم الإجمالية إلى 5-10% من
                  المبلغ المحوّل! اسأل دائماً: "كم سأدفع بالضبط؟ وكم سيستلم
                  المستفيد بالضبط؟"
                </div>
                <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                  <strong>مثال عملي:</strong>
                </h4>
                <p style={{ lineHeight: "1.8" }}>
                  لنفترض أنك تريد تحويل <strong>1000 دولار</strong> إلى يورو:
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>سعر الصرف الحقيقي: 1 USD = 0.92 EUR</li>
                  <li>
                    المبلغ المتوقع: 1000 × 0.92 = <strong>920 يورو</strong>
                  </li>
                  <li>سعر البنك: 1 USD = 0.88 EUR (فارق 4%)</li>
                  <li>ما ستحصل عليه: 1000 × 0.88 = 880 يورو</li>
                  <li>عمولة ثابتة: -10 يورو</li>
                  <li>
                    <strong>المبلغ النهائي: 870 يورو</strong>
                  </li>
                  <li>
                    <strong style={{ color: "#dc3545" }}>
                      الخسارة الإجمالية: 50 يورو (حوالي 5.4%)
                    </strong>
                  </li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  ما الفرق بين تحويل العملات والتحويل البنكي الدولي؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>تحويل العملات</strong> و<strong>التحويل البنكي الدولي</strong>{" "}
                  مفهومان مختلفان:
                </p>
                <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                  <strong>تحويل العملات (Currency Exchange):</strong>
                </h4>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>التعريف:</strong> عملية تبديل عملة بعملة أخرى
                  </li>
                  <li>
                    <strong>المكان:</strong> مكاتب الصرافة، البنوك، منصات إلكترونية
                  </li>
                  <li>
                    <strong>السرعة:</strong> فورية عادة
                  </li>
                  <li>
                    <strong>الاستخدام:</strong> للسفر، التسوق، الاستثمار
                  </li>
                  <li>
                    <strong>مثال:</strong> تحويل 100 دولار إلى يورو نقداً
                  </li>
                </ul>
                <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                  <strong>التحويل البنكي الدولي (Wire Transfer/Remittance):</strong>
                </h4>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>التعريف:</strong> إرسال أموال من بلد إلى آخر عبر البنوك
                  </li>
                  <li>
                    <strong>المكان:</strong> البنوك، شركات التحويل (ويسترن يونيون،
                    MoneyGram)
                  </li>
                  <li>
                    <strong>السرعة:</strong> من يوم إلى 5 أيام عمل
                  </li>
                  <li>
                    <strong>الاستخدام:</strong> إرسال أموال للعائلة، دفع فواتير،
                    مدفوعات تجارية
                  </li>
                  <li>
                    <strong>مثال:</strong> تحويل 1000 دولار من حسابك في أمريكا إلى
                    حساب في السعودية
                  </li>
                </ul>
                <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                  <strong>الفرق الرئيسي:</strong>
                </h4>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>تحويل العملات</strong> = تبديل عملة بأخرى (قد يكون
                  نقداً أو إلكترونياً)
                  <br />
                  <strong>التحويل البنكي</strong> = إرسال أموال من شخص/حساب لآخر
                  (عادة يتضمن تحويل عملات أيضاً)
                </p>
                <div
                  className="alert alert-info mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>💡 مثال توضيحي:</strong> إذا كنت في مصر وأرسلت 1000 دولار
                  لصديقك في تركيا عبر البنك، سيحدث الآتي:
                  <br />
                  1. <strong>تحويل بنكي</strong> من حسابك إلى حسابه
                  <br />
                  2. <strong>تحويل عملات</strong> من دولار إلى ليرة تركية
                  <br />
                  الأمران يحدثان معاً، لكنهما عمليتان مختلفتان!
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  هل محول العملات دقيق؟ وهل يمكنني الاعتماد عليه؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>محول العملات</strong> يوفر <strong>أسعار مرجعية دقيقة</strong>{" "}
                  بناءً على أسعار السوق العالمية، لكن هناك بعض النقاط المهمة:
                </p>
                <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                  <strong>✓ نقاط القوة:</strong>
                </h4>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>بيانات حقيقية:</strong> يستخدم أسعار من{" "}
                    <strong>سوق الفوركس</strong> العالمي
                  </li>
                  <li>
                    <strong>تحديث مستمر:</strong> الأسعار تتحدث كل بضع دقائق أو
                    ساعات
                  </li>
                  <li>
                    <strong>حسابات دقيقة:</strong> الرياضيات المستخدمة صحيحة 100%
                  </li>
                  <li>
                    <strong>مجاني:</strong> خدمة مجانية بدون أي تكلفة
                  </li>
                </ul>
                <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                  <strong>⚠️ محددات ونقاط يجب معرفتها:</strong>
                </h4>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>السعر المعروض ≠ السعر الفعلي في البنك:</strong> البنوك
                    تضيف هامش ربح (1-5%)
                  </li>
                  <li>
                    <strong>لا يتضمن الرسوم:</strong> المحول لا يحسب العمولات
                    والرسوم الإضافية
                  </li>
                  <li>
                    <strong>تأخر بسيط:</strong> قد يكون هناك تأخير بسيط (دقائق)
                    بين السعر الفعلي والمعروض
                  </li>
                  <li>
                    <strong>اختلاف الأسعار:</strong> سعر الشراء يختلف عن سعر البيع
                  </li>
                </ul>
                <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                  <strong>كيف تستخدم المحول بشكل صحيح؟</strong>
                </h4>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>للمرجع والتقدير:</strong> استخدم المحول لمعرفة السعر
                    التقريبي والتخطيط
                  </li>
                  <li>
                    <strong>للمقارنة:</strong> قارن السعر المعروض في المحول بما
                    يعرضه البنك - إذا كان الفرق كبيراً (أكثر من 5%)، ابحث عن بديل
                  </li>
                  <li>
                    <strong>تحقق قبل التنفيذ:</strong> قبل أي معاملة فعلية، اسأل
                    عن السعر النهائي مع جميع الرسوم
                  </li>
                  <li>
                    <strong>للتعليم:</strong> استخدمه لفهم كيف تتحرك العملات وتعلم
                    عن أسواق المال
                  </li>
                </ol>
                <div
                  className="alert alert-success mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>✓ الخلاصة:</strong> <strong>محول العملات</strong> دقيق
                  كأداة مرجعية وتعليمية ممتازة، لكن <strong>لا تعتبره</strong> السعر
                  النهائي الذي ستحصل عليه. استخدمه للتقدير والمقارنة، ثم تحقق من
                  الأسعار الفعلية قبل أي معاملة مالية حقيقية.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <br />
      {/* Hidden link for API attribution */}
      <div style={{ display: "none" }}>
        <a href="https://www.exchangerate-api.com">
          Rates By Exchange Rate API
        </a>
      </div>
    </div>
  );
}
export default CurrencyConverter;